import { useVoices } from '../../../api/voices'
import { VoiceSelect } from '../../../features/podcast-editor/components/voice-select/voice-select'
import type { VoiceOption } from '../../../features/podcast-editor/context/podcast-editor-provider'
import { LanguagePicker } from '../../language-picker'
import { useEffect, useMemo } from 'react'

interface VoiceStepProps {
  hostAVoiceId: string
  hostBVoiceId: string
  languageIso: string
  onHostAChange: (id: string) => void
  onHostBChange: (id: string) => void
  onLanguageChange: (iso: string) => void
}

export function VoiceStep({ hostAVoiceId, hostBVoiceId, languageIso, onHostAChange, onHostBChange, onLanguageChange }: VoiceStepProps) {
  // Filter voices server-side by the selected language so users only see voices that
  // actually speak the chosen language. The query re-runs reactively when languageIso changes.
  const { data: allVoices, isLoading } = useVoices(undefined, languageIso)

  const voiceOptions: VoiceOption[] = useMemo(() => {
    if (!allVoices) return []
    return allVoices.map(v => ({
      id: v.id,
      name: v.name,
      description: v.description ?? undefined,
      gender: v.gender ?? undefined,
      previewUrl: v.previewUrl ?? undefined,
      category: v.category,
    }))
  }, [allVoices])

  // When the language (and therefore the voice list) changes, ensure the currently selected
  // voices still exist in the filtered list. If not, auto-pick sensible defaults so the user
  // never ends up with an out-of-language voice silently surviving.
  useEffect(() => {
    if (isLoading || voiceOptions.length === 0) return

    const hasA = voiceOptions.some((v) => v.id === hostAVoiceId)
    const hasB = voiceOptions.some((v) => v.id === hostBVoiceId)
    if (hasA && hasB) return

    // Prefer one masculine + one feminine for variety; otherwise just take the first two.
    const masculine = voiceOptions.find((v) => v.gender?.toLowerCase() === 'masculine')
    const feminine = voiceOptions.find((v) => v.gender?.toLowerCase() === 'feminine')
    const fallbackA = masculine ?? voiceOptions[0]
    const fallbackB = feminine && feminine.id !== fallbackA.id
      ? feminine
      : voiceOptions.find((v) => v.id !== fallbackA.id) ?? fallbackA

    if (!hasA && fallbackA) onHostAChange(fallbackA.id)
    if (!hasB && fallbackB) onHostBChange(fallbackB.id)
  }, [voiceOptions, isLoading, hostAVoiceId, hostBVoiceId, onHostAChange, onHostBChange])

  return (
    <div>
      <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-foreground">Voices & language</h2>
      <p className="mb-8 text-base font-medium text-foreground/60">
        Pick who hosts your podcast and what language they speak. You can change these anytime.
      </p>

      <div className="space-y-5">
        <LanguagePicker
          label="Language"
          value={languageIso}
          onChange={onLanguageChange}
          size="md"
        />

        <div className="h-px bg-black/10" />

        <VoiceSelect
          label="Host A"
          value={hostAVoiceId}
          onChange={onHostAChange}
          options={voiceOptions}
          loading={isLoading}
        />
        <VoiceSelect
          label="Host B"
          value={hostBVoiceId}
          onChange={onHostBChange}
          options={voiceOptions}
          loading={isLoading}
        />
      </div>
    </div>
  )
}
